import { useEffect } from 'react';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getNextToolParameterValue, getToolConfigParams } from '@/tools/tool.utilities';
import {
  VALUE_SEARCH_CONFIG_VERSION,
  hasSameBetweenValues,
  isSimpleLowerBoundGreater,
  migrateValueSearchConfig,
} from '@/trendData/valueSearch.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { OPERATOR } from '@/toolSelection/investigate.constants';
import type {
  BaseToolPersistedState,
  DurationWithUnits,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

/** A limit is either a literal entered as text or the id of a signal or scalar picked from the item select. */
export type ValueSearchSelectableValue = string | number | { id: string } | null | undefined;

export interface ValueSearchState extends BaseToolPersistedState {
  /** Signal the search runs against, or `undefined` until one is picked. */
  inputSignal: ToolItem | undefined;
  /** `true` for the simple form, `false` for the advanced entry/exit form. */
  isSimple: boolean;
  /** Wraps the input signal in `.validValues()` so gaps are ignored. */
  useValidValues: boolean;
  /** Simple mode only: drop short capsules and merge short gaps using `minDuration` and `mergeDuration`. */
  isCleansing: boolean;
  /** Simple mode comparison applied to the input signal, or `undefined` until one is picked. */
  simpleOperator: OPERATOR | undefined;
  /** Threshold, or the upper limit when `simpleOperator` is a between operator. */
  simpleValue: ValueSearchSelectableValue;
  /** Between operators only: comparison applied at the upper limit (e.g. `<=` or `<`). */
  simpleUpperValueInclusivity: OPERATOR | undefined;
  /** Lower limit, used only when `simpleOperator` is a between operator. */
  simpleLowerValue: ValueSearchSelectableValue;
  /** Between operators only: comparison applied at the lower limit (e.g. `>=` or `>`). */
  simpleLowerValueInclusivity: OPERATOR | undefined;
  /** Capsules shorter than this are dropped when `isCleansing` is on. */
  minDuration: ValueWithUnitsItem;
  /** Gaps shorter than this are merged when `isCleansing` is on. */
  mergeDuration: ValueWithUnitsItem;
  /** Advanced mode comparison that starts a capsule, or `undefined` until one is picked. */
  advancedEntryOperator: OPERATOR | undefined;
  /** Entry threshold, or the upper limit when `advancedEntryOperator` is a between operator. */
  advancedEntryValue: ValueSearchSelectableValue;
  /** Lower limit when `advancedEntryOperator` is a between operator; the exit condition reuses it too. */
  advancedEntryLowerValue: ValueSearchSelectableValue;
  /** How long the entry condition must hold before a capsule starts; reused for exit with a between operator. */
  advancedEntryDuration: ValueWithUnitsItem;
  /** Advanced mode comparison that ends a capsule, or `undefined` until one is picked. */
  advancedExitOperator: OPERATOR | undefined;
  /** Exit threshold; ignored when `advancedEntryOperator` is a between operator, which reuses the entry limits. */
  advancedExitValue: ValueSearchSelectableValue;
  /** How long the exit condition must hold before a capsule ends; ignored with a between entry operator. */
  advancedExitDuration: ValueWithUnitsItem;
  /** Set when the saved item was upgraded from a Deviation Search, so the form can warn about it. */
  isMigratedDeviationSearch: boolean;
  /** Saved config version; see `migrateValueSearchConfig`. */
  version: string;
}

export type ValueSearchDehydratedState = ValueSearchState;

export interface ValueSearchActions {
  setSearchName: (name: string) => void;
  setInputSignal: (item: ToolItem | string | null | undefined) => void;
  setMaximumDuration: (value: number, units: string) => void;
  setIsSimple: (isSimple: boolean) => void;
  toggleCleansing: () => void;
  toggleValidValues: () => void;
  setSimpleOperator: (simpleOperator: OPERATOR | undefined) => void;
  setSimpleValue: (simpleValue: ValueSearchSelectableValue) => void;
  setSimpleUpperValueInclusivity: (simpleUpperValueInclusivity: OPERATOR) => void;
  setSimpleLowerValue: (simpleLowerValue: ValueSearchSelectableValue) => void;
  setSimpleLowerValueInclusivity: (simpleLowerValueInclusivity: OPERATOR) => void;
  setMinDuration: (minDuration: ValueWithUnitsItem) => void;
  setMergeDuration: (mergeDuration: ValueWithUnitsItem) => void;
  setAdvancedEntryOperator: (advancedEntryOperator: OPERATOR | undefined) => void;
  setAdvancedEntryValue: (advancedEntryValue: ValueSearchSelectableValue) => void;
  setAdvancedEntryLowerValue: (advancedEntryLowerValue: ValueSearchSelectableValue) => void;
  setAdvancedEntryDuration: (advancedEntryDuration: ValueWithUnitsItem) => void;
  setAdvancedExitOperator: (advancedExitOperator: OPERATOR | undefined) => void;
  setAdvancedExitValue: (advancedExitValue: ValueSearchSelectableValue) => void;
  setAdvancedExitDuration: (advancedExitDuration: ValueWithUnitsItem) => void;
  setIsMigratedDeviationSearch: (isMigratedDeviationSearch: boolean) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: ResettableStoreState<ValueSearchState>;
}

export const DEFAULT_DURATION: ValueWithUnitsItem = Object.freeze({ value: 0, units: 'min' });

const parameterDefinitions: ParameterDefinitions<'inputSignal'> = {
  inputSignal: { predicate: ['name', 'a'] },
};

const initialState: ValueSearchState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  inputSignal: undefined,
  isSimple: true,
  useValidValues: false,
  isCleansing: false,
  simpleOperator: undefined,
  simpleValue: null,
  simpleUpperValueInclusivity: undefined,
  simpleLowerValue: null,
  simpleLowerValueInclusivity: undefined,
  minDuration: DEFAULT_DURATION,
  mergeDuration: DEFAULT_DURATION,
  advancedEntryOperator: undefined,
  advancedEntryValue: null,
  advancedEntryLowerValue: null,
  advancedEntryDuration: DEFAULT_DURATION,
  advancedExitOperator: undefined,
  advancedExitValue: null,
  advancedExitDuration: DEFAULT_DURATION,
  isMigratedDeviationSearch: false,
  version: VALUE_SEARCH_CONFIG_VERSION,
});

export const dehydrateValueSearchState = (state: ValueSearchState): ValueSearchDehydratedState => state;

/** The UIConfig saved with a Value Search item. */
export function getValueSearchConfigParams(state: ValueSearchState): ToolConfigParams {
  return getToolConfigParams({
    serializedState: state as unknown as Record<string, ToolConfigValue>,
    toolType: TREND_TOOLS.VALUE_SEARCH,
    parameterDefinitionKeys: Object.keys(parameterDefinitions),
    advancedParametersCollapsed: state.advancedParametersCollapsed,
  });
}

const valueSearchActionsFactory: ActionsFactory<ValueSearchState, ValueSearchActions> = (set, _get, resetState) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setInputSignal: (item) =>
    set((draft) => {
      const next = getNextToolParameterValue({
        definition: parameterDefinitions.inputSignal,
        currentValue: draft.inputSignal,
        item: typeof item === 'string' ? { id: item } : item,
      });
      if (next.changed) {
        draft.inputSignal = (next.value as ToolItem | null | undefined) ?? undefined;
      }
    }),

  setMaximumDuration: (value, units) =>
    set((draft) => {
      draft.maximumDuration = { units, value } as DurationWithUnits;
    }),

  setIsSimple: (isSimple) =>
    set((draft) => {
      draft.isSimple = isSimple;
    }),

  toggleCleansing: () =>
    set((draft) => {
      draft.isCleansing = !draft.isCleansing;
    }),

  toggleValidValues: () =>
    set((draft) => {
      draft.useValidValues = !draft.useValidValues;
    }),

  setSimpleOperator: (simpleOperator) =>
    set((draft) => {
      draft.simpleOperator = simpleOperator;
    }),

  setSimpleValue: (simpleValue) =>
    set((draft) => {
      draft.simpleValue = simpleValue;
    }),

  setSimpleUpperValueInclusivity: (simpleUpperValueInclusivity) =>
    set((draft) => {
      draft.simpleUpperValueInclusivity = simpleUpperValueInclusivity;
    }),

  setSimpleLowerValue: (simpleLowerValue) =>
    set((draft) => {
      draft.simpleLowerValue = simpleLowerValue;
    }),

  setSimpleLowerValueInclusivity: (simpleLowerValueInclusivity) =>
    set((draft) => {
      draft.simpleLowerValueInclusivity = simpleLowerValueInclusivity;
    }),

  setMinDuration: (minDuration) =>
    set((draft) => {
      draft.minDuration = minDuration;
    }),

  setMergeDuration: (mergeDuration) =>
    set((draft) => {
      draft.mergeDuration = mergeDuration;
    }),

  setAdvancedEntryOperator: (advancedEntryOperator) =>
    set((draft) => {
      draft.advancedEntryOperator = advancedEntryOperator;
    }),

  setAdvancedEntryValue: (advancedEntryValue) =>
    set((draft) => {
      draft.advancedEntryValue = advancedEntryValue;
    }),

  setAdvancedEntryLowerValue: (advancedEntryLowerValue) =>
    set((draft) => {
      draft.advancedEntryLowerValue = advancedEntryLowerValue;
    }),

  setAdvancedEntryDuration: (advancedEntryDuration) =>
    set((draft) => {
      draft.advancedEntryDuration = advancedEntryDuration;
    }),

  setAdvancedExitOperator: (advancedExitOperator) =>
    set((draft) => {
      draft.advancedExitOperator = advancedExitOperator;
    }),

  setAdvancedExitValue: (advancedExitValue) =>
    set((draft) => {
      draft.advancedExitValue = advancedExitValue;
    }),

  setAdvancedExitDuration: (advancedExitDuration) =>
    set((draft) => {
      draft.advancedExitDuration = advancedExitDuration;
    }),

  setIsMigratedDeviationSearch: (isMigratedDeviationSearch) =>
    set((draft) => {
      draft.isMigratedDeviationSearch = isMigratedDeviationSearch;
    }),

  /**
   * Loads a saved Value Search item into the form. Older UIConfigs are upgraded first. A limit that was a signal or
   * scalar comes back as formula parameter `b` (upper) or `c` (lower), so its id replaces the saved limit.
   */
  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      Object.assign(draft, initialState, migrateValueSearchConfig(rehydrationState.config), {
        originalParameters: rehydrationState.originalParameters,
        inputSignal: rehydrationState.selectedParameters.inputSignal,
      });

      const simpleValueId = payload.parameters?.find((parameter) => parameter.name === 'b')?.item?.id;
      if (simpleValueId) {
        draft.simpleValue = simpleValueId;
      }
      const simpleLowerValueId = payload.parameters?.find((parameter) => parameter.name === 'c')?.item?.id;
      if (simpleLowerValueId) {
        draft.simpleLowerValue = simpleLowerValueId;
      }
    }),

  reset: resetState,
});

const STORE_NAME = 'ValueSearch';

const [ValueSearchStoreProvider, useValueSearchStoreInstance, useValueSearchStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  valueSearchActionsFactory,
);

export { ValueSearchStoreProvider };

export const useValueSearchId = () => useValueSearchStoreSelector((state) => state.id);
export const useValueSearchName = () => useValueSearchStoreSelector((state) => state.name);
export const useValueSearchOriginalParameters = () => useValueSearchStoreSelector((state) => state.originalParameters);
/** The saved maximum capsule duration, or `undefined` when the system default applies. */
export const useValueSearchMaximumDuration = () => useValueSearchStoreSelector((state) => state.maximumDuration);
export const useValueSearchInputSignal = () => useValueSearchStoreSelector((state) => state.inputSignal);
export const useValueSearchIsSimple = () => useValueSearchStoreSelector((state) => state.isSimple);
export const useValueSearchUseValidValues = () => useValueSearchStoreSelector((state) => state.useValidValues);
export const useValueSearchIsCleansing = () => useValueSearchStoreSelector((state) => state.isCleansing);
export const useValueSearchSimpleOperator = () => useValueSearchStoreSelector((state) => state.simpleOperator);
export const useValueSearchSimpleValue = () => useValueSearchStoreSelector((state) => state.simpleValue);
export const useValueSearchSimpleUpperValueInclusivity = () =>
  useValueSearchStoreSelector((state) => state.simpleUpperValueInclusivity);
export const useValueSearchSimpleLowerValue = () => useValueSearchStoreSelector((state) => state.simpleLowerValue);
export const useValueSearchSimpleLowerValueInclusivity = () =>
  useValueSearchStoreSelector((state) => state.simpleLowerValueInclusivity);
export const useValueSearchMinDuration = () => useValueSearchStoreSelector((state) => state.minDuration);
export const useValueSearchMergeDuration = () => useValueSearchStoreSelector((state) => state.mergeDuration);
export const useValueSearchAdvancedEntryOperator = () =>
  useValueSearchStoreSelector((state) => state.advancedEntryOperator);
export const useValueSearchAdvancedEntryValue = () => useValueSearchStoreSelector((state) => state.advancedEntryValue);
export const useValueSearchAdvancedEntryLowerValue = () =>
  useValueSearchStoreSelector((state) => state.advancedEntryLowerValue);
export const useValueSearchAdvancedEntryDuration = () =>
  useValueSearchStoreSelector((state) => state.advancedEntryDuration);
export const useValueSearchAdvancedExitOperator = () =>
  useValueSearchStoreSelector((state) => state.advancedExitOperator);
export const useValueSearchAdvancedExitValue = () => useValueSearchStoreSelector((state) => state.advancedExitValue);
export const useValueSearchAdvancedExitDuration = () =>
  useValueSearchStoreSelector((state) => state.advancedExitDuration);
export const useValueSearchIsMigratedDeviationSearch = () =>
  useValueSearchStoreSelector((state) => state.isMigratedDeviationSearch);
/** True when the active form's between operator has the same upper and lower value. */
export const useValueSearchHasSameBetweenValues = () => useValueSearchStoreSelector(hasSameBetweenValues);
/** True when the simple form's lower limit is greater than its upper limit. */
export const useValueSearchIsSimpleLowerBoundGreater = () => useValueSearchStoreSelector(isSimpleLowerBoundGreater);
export const useValueSearchActions = () => useValueSearchStoreInstance().actions;

/** @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. */
export function legacyCreateValueSearchStore() {
  return createStoreInstance<ValueSearchState, ValueSearchActions>(initialState, valueSearchActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistValueSearchStore = () => {
  const register = useRegisterPersistedStore();
  const store = useValueSearchStoreInstance();

  useEffect(
    () =>
      register({
        storeName: 'sqValueSearchStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateValueSearchState,
      }),
    [register, store],
  );
};
